'use client'

import { signIn } from 'next-auth/react'
import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { FcGoogle } from 'react-icons/fc'
import { useSearchParams, useRouter } from 'next/navigation'
import Link from 'next/link'

export default function LoginPage() {
  const [step, setStep] = useState<1 | 2>(1)
  const [phone, setPhone] = useState('')
  const [otp, setOtp] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [resendCountdown, setResendCountdown] = useState(0)
  
  const isOtpEnabled = process.env.NEXT_PUBLIC_ENABLE_OTP === 'true'

  const searchParams = useSearchParams()
  const router = useRouter()
  const callbackUrl = searchParams.get('callbackUrl') || '/auth/redirect'

  useEffect(() => {
    let timer: NodeJS.Timeout
    if (resendCountdown > 0) {
      timer = setTimeout(() => setResendCountdown(c => c - 1), 1000)
    }
    return () => clearTimeout(timer)
  }, [resendCountdown])

  const handleGoogleSignIn = async () => {
    setIsLoading(true)
    try {
      await signIn('google', { callbackUrl, redirect: true })
    } catch (error) {
      console.error('Sign in error:', error)
      setIsLoading(false)
    }
  }

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMsg('')
    if (!/^[6-9]\d{9}$/.test(phone)) {
      setErrorMsg('Please enter a valid 10-digit Indian phone number')
      return
    }

    setIsLoading(true)
    try {
      const res = await fetch('/api/auth/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to send OTP')
      
      setStep(2)
      setResendCountdown(30)
    } catch (err: any) {
      setErrorMsg(err.message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMsg('')
    if (otp.length !== 6) {
      setErrorMsg('OTP must be 6 digits')
      return
    }

    setIsLoading(true)
    try {
      const result = await signIn('credentials', {
        phone,
        otp,
        callbackUrl,
        redirect: false
      })

      if (result?.error) {
        setErrorMsg(result.error)
      } else {
        router.push(callbackUrl)
      }
    } catch (err: any) {
      setErrorMsg('An unexpected error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendOTP = async () => {
    if (resendCountdown > 0) return
    handleSendOTP({ preventDefault: () => {} } as React.FormEvent)
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
          <div className="text-center">
            <Link href="/" className="inline-block hover:opacity-80 transition-opacity">
              <h1 className="text-3xl font-bold text-gray-900">Mockzy</h1>
            </Link>
            <p className="mt-2 text-gray-600">Sign in to access your dashboard</p>
          </div>

          {errorMsg && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600 text-center">{errorMsg}</p>
            </div>
          )}
          {searchParams.get('error') && !errorMsg && (
             <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
               <p className="text-sm text-red-600 text-center">
                 {searchParams.get('error') === 'OAuthAccountNotLinked'
                   ? 'Email already in use with different provider'
                   : 'Something went wrong. Please try again.'}
               </p>
             </div>
          )}

          {isOtpEnabled && (
            <>
              {step === 1 ? (
                <form onSubmit={handleSendOTP} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                        +91
                      </span>
                      <input
                        type="tel"
                        maxLength={10}
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                        className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="Enter 10-digit number"
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                  <Button type="submit" disabled={isLoading || phone.length !== 10} className="w-full">
                    {isLoading ? 'Sending...' : 'Send OTP'}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleVerifyOTP} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Enter OTP sent to +91 ******{phone.slice(-4)}
                    </label>
                    <input
                      type="text"
                      maxLength={6}
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-center tracking-[0.5em] text-lg font-mono"
                      placeholder="------"
                      disabled={isLoading}
                    />
                  </div>
                  <Button type="submit" disabled={isLoading || otp.length !== 6} className="w-full">
                    {isLoading ? 'Verifying...' : 'Verify & Login'}
                  </Button>
                  <div className="flex justify-between text-sm mt-2">
                    <button
                      type="button"
                      onClick={() => { setStep(1); setOtp(''); setErrorMsg('') }}
                      className="text-indigo-600 hover:text-indigo-500"
                    >
                      Change number
                    </button>
                    <button
                      type="button"
                      onClick={handleResendOTP}
                      disabled={resendCountdown > 0 || isLoading}
                      className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
                    >
                      {resendCountdown > 0 ? `Resend OTP in ${resendCountdown}s` : 'Resend OTP'}
                    </button>
                  </div>
                </form>
              )}

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or continue with</span>
                </div>
              </div>
            </>
          )}

          <Button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            className="w-full h-12 text-base font-medium bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-300 shadow-sm flex items-center justify-center gap-3"
          >
            <FcGoogle className="w-6 h-6" />
            <span>{isOtpEnabled ? 'Google' : 'Continue with Google'}</span>
          </Button>
          
          {isOtpEnabled && (
            <p className="mt-6 text-center text-xs text-gray-500">
              Note: Google login will be disabled in 15 days. Please switch to phone login.
            </p>
          )}

          {!isOtpEnabled && (
            <div className="mt-8 space-y-4">
              <p className="text-sm text-center text-gray-500">By signing in, you get access to:</p>
              <ul className="space-y-3 text-sm text-gray-600">
                <li className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                    <svg className="w-3.5 h-3.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  200,000+ practice questions
                </li>
                <li className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                    <svg className="w-3.5 h-3.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  Timed mock tests
                </li>
                <li className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                    <svg className="w-3.5 h-3.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  Performance analytics
                </li>
                <li className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                    <svg className="w-3.5 h-3.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  Leaderboard rankings
                </li>
              </ul>
            </div>
          )}
        </div>
        
        {!isOtpEnabled && (
          <p className="mt-6 text-center text-sm text-gray-500">
            Don't have an account? Sign in with Google to get started!
          </p>
        )}
      </div>
    </div>
  )
}